import type { ReactNode } from "react";
import type { z } from "zod/v4";
import type {
  MaxConnectSchema,
  NextStepCardSchema,
  NextStepEventSchema,
  NextStepsLayout,
  OnboardingSchema,
  TelegramConnectSchema,
} from "@/schemas/sections";
import type { PostSubmitContext } from "@/types/config";
import { sanitizePostSubmit } from "@/utils/post-submit-context";

/*
 * How the section decides what it is looking at and therefore what to say.
 *
 * `readPageContext` sniffs the URL and referrer; `contextFromPostSubmit` uses
 * the structured hand-off instead when the form left one behind, which is the
 * reliable path. `resolveContent` then picks the copy set. Split out because
 * this is the section's only branching logic and it is pure enough to test
 * without rendering a card.
 */

export interface PageContext {
  email: string;
  source: string;
  kind: string;
  // SSR-resolved (token-keyed): the visitor is already bound to the bot. When
  // true the Telegram-connect module is not rendered at all — no re-link CTA.
  botConnected: boolean;
  connectTokenAvailable: boolean;
}

/** True when the referrer is a same-origin `/webinars/*` page — the fallback
 *  webinar signal when the server didn't set `?k=` (e.g. a legacy DOI-webinar
 *  forward). The authoritative signal is the `?k=` param from
 *  `funnel_objects.metadata.kind`; this only fills the gap. */
export function isWebinarReferrer(): boolean {
  try {
    const ref = document.referrer;
    if (!ref) return false;
    const url = new URL(ref, window.location.origin);
    return url.origin === window.location.origin && url.pathname.startsWith("/webinars/");
  } catch {
    return false;
  }
}

/** Client-side fallback: read + validate the post-submit context from the URL
 *  (used only when the server didn't supply `postSubmit` — soft-nav / prerender).
 *  Applies the same allow-listing as the server via the shared sanitizer, plus
 *  the `/webinars/` referrer webinar upgrade. */
export function readPageContext(): PageContext {
  // Client-only fallback (soft-nav / prerender): the URL can't tell us whether
  // the visitor is already connected, so default `botConnected` to false — the
  // safe default is to SHOW the connect module.
  if (typeof window === "undefined") {
    return {
      email: "",
      source: "",
      kind: "",
      botConnected: false,
      connectTokenAvailable: false,
    };
  }
  const params = new URLSearchParams(window.location.search);
  const ctx = sanitizePostSubmit({
    email: params.get("email"),
    p: params.get("p"),
    k: params.get("k"),
    t: params.get("t"),
  });
  const base: PageContext = {
    ...ctx,
    botConnected: false,
    connectTokenAvailable: ctx.connectTokenAvailable === true,
  };
  if (!base.kind && isWebinarReferrer()) return { ...base, kind: "webinar" };
  return base;
}

export function contextFromPostSubmit(ps?: PostSubmitContext | null): PageContext {
  return {
    email: ps?.email ?? "",
    source: ps?.source ?? "",
    kind: ps?.kind ?? "",
    botConnected: ps?.botConnected === true,
    connectTokenAvailable: ps?.connectTokenAvailable === true,
  };
}

export function renderSubtitle(template: string, email: string, fallback?: string): ReactNode {
  if (!email) {
    return fallback ?? template.replace(/\s*\{email\}/g, "").trim();
  }
  if (!template.includes("{email}")) {
    return template;
  }
  const [before, after = ""] = template.split("{email}");
  return (
    <>
      {before}
      <strong className="font-(weight:--fw-bold) text-fg">{email}</strong>
      {after}
    </>
  );
}

export interface ResolvedContent {
  title: string;
  subtitle: string;
  subtitleNoEmail?: string;
  spamTitle: string;
  spamNote: string;
  steps: string[];
  nextTitle: string;
  nextSubtitle?: string;
  telegramConnect?: z.input<typeof TelegramConnectSchema>;
  maxConnect?: z.input<typeof MaxConnectSchema>;
  onboarding?: z.input<typeof OnboardingSchema>;
  event?: z.input<typeof NextStepEventSchema>;
  nextStepsLayout: NextStepsLayout;
  cards: z.input<typeof NextStepCardSchema>[];
}

/** Layer content: defaults ← byKind[kind] ← bySource[source] (most specific wins). */
export function resolveContent(
  defaults: ResolvedContent,
  kindOv: Partial<ResolvedContent> | undefined,
  sourceOv: Partial<ResolvedContent> | undefined,
): ResolvedContent {
  return {
    title: sourceOv?.title ?? kindOv?.title ?? defaults.title,
    subtitle: sourceOv?.subtitle ?? kindOv?.subtitle ?? defaults.subtitle,
    subtitleNoEmail:
      sourceOv?.subtitleNoEmail ?? kindOv?.subtitleNoEmail ?? defaults.subtitleNoEmail,
    spamTitle: sourceOv?.spamTitle ?? kindOv?.spamTitle ?? defaults.spamTitle,
    spamNote: sourceOv?.spamNote ?? kindOv?.spamNote ?? defaults.spamNote,
    steps: sourceOv?.steps ?? kindOv?.steps ?? defaults.steps,
    nextTitle: sourceOv?.nextTitle ?? kindOv?.nextTitle ?? defaults.nextTitle,
    nextSubtitle: sourceOv?.nextSubtitle ?? kindOv?.nextSubtitle ?? defaults.nextSubtitle,
    telegramConnect:
      sourceOv?.telegramConnect ?? kindOv?.telegramConnect ?? defaults.telegramConnect,
    maxConnect: sourceOv?.maxConnect ?? kindOv?.maxConnect ?? defaults.maxConnect,
    onboarding: sourceOv?.onboarding ?? kindOv?.onboarding ?? defaults.onboarding,
    // `event` is authored on bySource by design: it is a property of ONE webinar,
    // and this page is shared by every lead magnet and every webinar. A visitor
    // whose `source` we could not resolve (the `/webinars/` referrer upgrade sets
    // `kind` but leaves `source` empty) therefore gets no card — no date beats a
    // wrong date.
    event: sourceOv?.event ?? kindOv?.event ?? defaults.event,
    nextStepsLayout:
      sourceOv?.nextStepsLayout ?? kindOv?.nextStepsLayout ?? defaults.nextStepsLayout,
    cards: sourceOv?.cards ?? kindOv?.cards ?? defaults.cards,
  };
}

/** Resolve the page context: server-provided `postSubmit` (SSR + hydration, no
 *  flash), falling back to the client URL only when absent, plus the `/webinars/`
 *  referrer webinar upgrade. Fires the landing tracking event once on mount. */
